import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, Container, TablePagination, Tooltip, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import {
  downloadAuthenticatedFile,
  SessionExpiredError
} from 'src/utils/authenticatedFetch';
import { useParams } from 'react-router-dom';

const getCurrentMonthFirstDay = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
};

const formatDateShort = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

/** First day of start month through last day of end month (end month cannot precede start). */
const getMonthPeriodRange = (startMonth: Date, endMonth: Date) => {
    const y1 = startMonth.getFullYear();
    const m1 = startMonth.getMonth();
    const y2 = endMonth.getFullYear();
    const m2 = endMonth.getMonth();
    const firstStart = new Date(y1, m1, 1);
    const firstEnd = new Date(y2, m2, 1);
    const rangeStart = firstStart;
    const rangeEndMonthStart = firstEnd < firstStart ? firstStart : firstEnd;
    return {
        startDate: formatDateShort(rangeStart),
        endDate: formatDateShort(
            new Date(rangeEndMonthStart.getFullYear(), rangeEndMonthStart.getMonth() + 1, 0),
        ),
    };
};

const IntactPets = () => {
    const [pets, setPets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [startMonth, setStartMonth] = useState<Date>(getCurrentMonthFirstDay());
    const [endMonth, setEndMonth] = useState<Date>(getCurrentMonthFirstDay());
    const { permit } = useParams();

    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const { startDate, endDate } = useMemo(
        () => getMonthPeriodRange(startMonth, endMonth),
        [startMonth, endMonth],
    );

    const columns = [
        { field: 'LicenseNumber', headerName: 'License' },
        { field: 'PetName', headerName: 'Pet Name' },
        { field: 'PetType', headerName: 'Pet Type' },
        { field: 'OwnerName', headerName: 'Owner Name' },
    ];

    const fetchPets = async () => {
        setLoading(true);
        try {
            const response = await httpRequest(
                'GET',
                `/pet/report/licensedPets?isSpayedNeutered=false&startDate=${startDate}&endDate=${endDate}&Page=${currentPage + 1}&Size=${itemsPerPage}`,
            );
            if (response && response.status === "success" && response.data) {
                setPets(response.data.data || []);
                setTotalRecords(response.data.totalRecords || 0);
            } else {
                setPets([]);
            }
        } catch (error) {
            console.error('API request failed', error);
            setPets([]);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const handleStartMonthChange = (newValue: Date | null) => {
        if (newValue) {
            setStartMonth(new Date(newValue.getFullYear(), newValue.getMonth(), 1));
            setCurrentPage(0);
        }
    };

    const handleEndMonthChange = (newValue: Date | null) => {
        if (newValue) {
            setEndMonth(new Date(newValue.getFullYear(), newValue.getMonth(), 1));
            setCurrentPage(0);
        }
    };

    const handleDownload = async () => {
        try {
            await downloadAuthenticatedFile(
                `/pet/report/${permit}/download_licensed_pets?reportType=intact&startDate=${startDate}&endDate=${endDate}`,
                `intact_pets_${startDate}_${endDate}.pdf`
            );
            toast.success('Report downloaded successfully');
        } catch (error: any) {
            if (error instanceof SessionExpiredError) {
                return;
            }
            toast.error(error?.message || 'Failed to download report');
        }
    };

    useEffect(() => {
        fetchPets();
    }, [currentPage, itemsPerPage, startDate, endDate]);

    return (
        <Box>
            <Typography variant="h3" mx={3} sx={{ color: 'primary.main' }}>
                Intact Pets
            </Typography>
            <Container maxWidth="xl">
                <Paper sx={{ p: 2, mb: 3, mt: 1 }}>
                    <Grid container spacing={2} py={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    views={['month', 'year']}
                                    label="From (month)"
                                    value={startMonth}
                                    onChange={handleStartMonthChange}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    views={['month', 'year']}
                                    label="To (month)"
                                    value={endMonth}
                                    onChange={handleEndMonthChange}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={12} md={4}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="body1">From:</Typography>
                                <Typography variant="h6" color="primary">{startDate}</Typography>
                                <Typography variant="body1">To:</Typography>
                                <Typography variant="h6" color="primary">{endDate}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Typography variant="body2" color="text.secondary">
                                Total Intact Pets: <strong>{totalRecords}</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2} textAlign={{ xs: 'center', md: 'right' }}>
                            <Tooltip title="Download Report" arrow>
                                <IconButton color="success" onClick={handleDownload}>
                                    <DownloadForOfflineIcon fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    {columns.map((column) => (
                                        <TableCell key={column.field} sx={{ textTransform: 'none' }}>
                                            {column.headerName}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">Loading...</TableCell>
                                    </TableRow>
                                ) : pets?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    pets?.map(pet => (
                                        <TableRow key={pet.ApplicationID || pet.PetID}>
                                            <TableCell>{pet.LicenseNumber || '-'}</TableCell>
                                            <TableCell>{pet.PetName || '-'}</TableCell>
                                            <TableCell>{pet.PetType || '-'}</TableCell>
                                            <TableCell>{pet.OwnerName || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalRecords}
                        page={currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={itemsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default IntactPets;
